import React, { useState, Children } from 'react';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/Button';
import { motion } from 'framer-motion';
import { Users, Music, Coffee, Camera, BookOpen, Baby, Globe, HandHeart, CheckCircle, Send, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
const teams = [{
  id: 'worship',
  name: 'Worship Team',
  description: 'Lead the congregation in praise and worship through music, vocals, and instruments.',
  icon: Music,
  roles: ['Vocalist', 'Musician', 'Choir Member']
}, {
  id: 'hospitality',
  name: 'Hospitality & Greeters',
  description: 'Create a welcoming environment for everyone who walks through our doors.',
  icon: Coffee,
  roles: ['Greeter', 'Usher', 'Café Team']
}, {
  id: 'media',
  name: 'Media & Production',
  description: 'Manage audio, visual, lighting, and broadcasting during services and events.',
  icon: Camera,
  roles: ['Sound Engineer', 'Camera Operator', 'Visuals/Lyrics']
}, {
  id: 'kids',
  name: 'Kids Ministry',
  description: 'Teach and care for the next generation in a safe, fun, and biblical environment.',
  icon: Baby,
  roles: ['Teacher', 'Assistant', 'Check-in Team']
}, {
  id: 'prayer',
  name: 'Prayer Ministry',
  description: 'Intercede for the church, community, and specific prayer requests.',
  icon: HandHeart,
  roles: ['Prayer Warrior', 'Altar Ministry']
}, {
  id: 'outreach',
  name: 'Community Outreach',
  description: 'Serve our local community through various outreach programs and events.',
  icon: Globe,
  roles: ['Event Volunteer', 'Community Liaison']
}];
export function JoinTeamPage() {
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const toggleTeam = (teamId: string) => {
    setSelectedTeams(prev => prev.includes(teamId) ? prev.filter(id => id !== teamId) : [...prev, teamId]);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedTeams.length === 0) {
      alert('Please select at least one team you are interested in.');
      return;
    }
    // Simulate API call
    console.log('Form submitted:', {
      ...formData,
      teams: selectedTeams
    });
    setSubmitted(true);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  if (submitted) {
    return <Layout data-id="element-700">
        <div className="min-h-[70vh] flex items-center justify-center bg-[#F5F7FA]" data-id="element-701">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="bg-white p-10 md:p-16 rounded-2xl shadow-xl max-w-2xl w-full text-center mx-4" data-id="element-702">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-703">
              <CheckCircle className="w-10 h-10 text-green-600" data-id="element-704" />
            </div>
            <h1 className="text-3xl md:text-4xl font-serif font-bold text-[#0A1F44] mb-4" data-id="element-705">
              Thank You!
            </h1>
            <p className="text-gray-600 text-lg mb-8" data-id="element-706">
              We're excited that you want to serve! A ministry leader will reach
              out to you soon to discuss next steps.
            </p>
            <Link to="/ministries" data-id="element-707">
              <Button rightIcon={<ArrowRight className="w-4 h-4" data-id="element-709" />} data-id="element-708">
                Explore Our Ministries
              </Button>
            </Link>
          </motion.div>
        </div>
      </Layout>;
  }
  return <Layout data-id="element-710">
      {/* Hero Section */}
      <section className="relative bg-[#0A1F44] pt-44 md:pt-48 pb-20 text-white overflow-hidden" data-id="element-711">
        <div className="absolute inset-0 opacity-20" data-id="element-712">
          <img src="https://images.unsplash.com/photo-1529156069898-49953e39b3ac?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80" alt="Team serving together" className="w-full h-full object-cover" data-id="element-713" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A1F44]/80 to-[#0A1F44]" data-id="element-714" />

        <div className="container-custom relative z-10" data-id="element-715">
          <div className="max-w-3xl" data-id="element-716">
            <motion.span initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="inline-block text-[#C8102E] text-sm font-semibold tracking-widest uppercase mb-4" data-id="element-717">
              Volunteer
            </motion.span>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold mb-6" data-id="element-718">
              Join a Team
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="text-xl text-white/80 leading-relaxed" data-id="element-719">
              God has given each of us unique gifts to build up the church.
              Discover your purpose by serving others in one of our ministries.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Why Serve Section */}
      <section className="py-16 bg-white border-b" data-id="element-720">
        <div className="container-custom" data-id="element-721">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto" data-id="element-722">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-center" data-id="element-723">
              <div className="w-16 h-16 bg-[#F5F7FA] rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-724">
                <Users className="w-8 h-8 text-[#0A1F44]" data-id="element-725" />
              </div>
              <h3 className="text-xl font-bold text-[#0A1F44] mb-2" data-id="element-726">
                Find Community
              </h3>
              <p className="text-gray-600" data-id="element-727">
                Serving alongside others is one of the best ways to build
                lasting relationships.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="text-center" data-id="element-728">
              <div className="w-16 h-16 bg-[#F5F7FA] rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-729">
                <BookOpen className="w-8 h-8 text-[#0A1F44]" data-id="element-730" />
              </div>
              <h3 className="text-xl font-bold text-[#0A1F44] mb-2" data-id="element-731">
                Grow Spiritually
              </h3>
              <p className="text-gray-600" data-id="element-732">
                As you pour out into others, you'll find yourself growing deeper
                in your own faith.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="text-center" data-id="element-733">
              <div className="w-16 h-16 bg-[#F5F7FA] rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-734">
                <Globe className="w-8 h-8 text-[#0A1F44]" data-id="element-735" />
              </div>
              <h3 className="text-xl font-bold text-[#0A1F44] mb-2" data-id="element-736">
                Make an Impact
              </h3>
              <p className="text-gray-600" data-id="element-737">
                Your unique gifts and talents can make an eternal difference in
                someone's life.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Application Form Section */}
      <section className="py-20 bg-[#F5F7FA]" data-id="element-738">
        <div className="container-custom" data-id="element-739">
          <div className="max-w-4xl mx-auto" data-id="element-740">
            <div className="text-center mb-12" data-id="element-741">
              <h2 className="text-3xl font-serif font-bold text-[#0A1F44] mb-4" data-id="element-742">
                Where would you like to serve?
              </h2>
              <p className="text-gray-600" data-id="element-743">
                Select one or more teams you're interested in joining, then fill
                out your details below.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="bg-white p-8 md:p-12 rounded-2xl shadow-lg" data-id="element-744">
              {/* Team Selection Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12" data-id="element-745">
                {teams.map((team, index) => {
                const isSelected = selectedTeams.includes(team.id);
                const Icon = team.icon;
                return <motion.button key={team.id} initial={{
                  opacity: 0,
                  y: 20
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  delay: index * 0.05
                }} onClick={() => toggleTeam(team.id)} type="button" className={`relative p-6 rounded-xl text-left transition-all ${isSelected ? 'bg-[#0A1F44] text-white shadow-lg scale-[1.02]' : 'bg-white hover:shadow-md border border-gray-100'}`} data-id="element-746">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 ${isSelected ? 'bg-[#C8102E]' : 'bg-[#F5F7FA]'}`} data-id="element-747">
                        <Icon className={`w-6 h-6 ${isSelected ? 'text-white' : 'text-[#0A1F44]'}`} data-id="element-748" />
                      </div>
                      <h3 className={`font-bold mb-2 ${isSelected ? 'text-white' : 'text-[#0A1F44]'}`} data-id="element-749">
                        {team.name}
                      </h3>
                      <p className={`text-sm mb-3 ${isSelected ? 'text-white/80' : 'text-gray-600'}`} data-id="element-750">
                        {team.description}
                      </p>
                      <div className="flex flex-wrap gap-1" data-id="element-751">
                        {team.roles.slice(0, 2).map(role => <span key={role} className={`text-xs px-2 py-0.5 rounded-full ${isSelected ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600'}`} data-id="element-752">
                            {role}
                          </span>)}
                        {team.roles.length > 2 && <span className={`text-xs px-2 py-0.5 rounded-full ${isSelected ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600'}`} data-id="element-753">
                            +{team.roles.length - 2} more
                          </span>}
                      </div>
                      {isSelected && <div className="absolute top-4 right-4" data-id="element-754">
                          <CheckCircle className="w-6 h-6 text-[#C8102E]" data-id="element-755" />
                        </div>}
                    </motion.button>;
              })}
              </div>

              <hr className="border-gray-200 mb-12" data-id="element-756" />

              {/* Personal Details */}
              <div className="space-y-6 mb-8" data-id="element-757">
                <h3 className="text-2xl font-serif font-bold text-[#0A1F44] mb-6" data-id="element-758">
                  Your Details
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-759">
                  <div data-id="element-760">
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-761">
                      First Name *
                    </label>
                    <input type="text" id="firstName" name="firstName" required value={formData.firstName} onChange={handleInputChange} className="w-full px-4 py-3 bg-[#F5F7FA] border border-[#E8ECF0] rounded-lg focus:outline-none focus:border-[#0A1F44] focus:ring-1 focus:ring-[#0A1F44] transition-colors" data-id="element-762" />
                  </div>
                  <div data-id="element-763">
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-764">
                      Last Name *
                    </label>
                    <input type="text" id="lastName" name="lastName" required value={formData.lastName} onChange={handleInputChange} className="w-full px-4 py-3 bg-[#F5F7FA] border border-[#E8ECF0] rounded-lg focus:outline-none focus:border-[#0A1F44] focus:ring-1 focus:ring-[#0A1F44] transition-colors" data-id="element-765" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-766">
                  <div data-id="element-767">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-768">
                      Email Address *
                    </label>
                    <input type="email" id="email" name="email" required value={formData.email} onChange={handleInputChange} className="w-full px-4 py-3 bg-[#F5F7FA] border border-[#E8ECF0] rounded-lg focus:outline-none focus:border-[#0A1F44] focus:ring-1 focus:ring-[#0A1F44] transition-colors" data-id="element-769" />
                  </div>
                  <div data-id="element-770">
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-771">
                      Phone Number
                    </label>
                    <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleInputChange} className="w-full px-4 py-3 bg-[#F5F7FA] border border-[#E8ECF0] rounded-lg focus:outline-none focus:border-[#0A1F44] focus:ring-1 focus:ring-[#0A1F44] transition-colors" data-id="element-772" />
                  </div>
                </div>

                {selectedTeams.length > 0 && <div data-id="element-773">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-774">
                      Selected Teams
                    </label>
                    <div className="flex flex-wrap gap-2" data-id="element-775">
                      {selectedTeams.map(teamId => {
                    const team = teams.find(t => t.id === teamId);
                    return team ? <span key={teamId} className="inline-flex items-center gap-1 bg-[#0A1F44] text-white px-3 py-1 rounded-full text-sm" data-id="element-776">
                            {team.name}
                            <button type="button" onClick={() => toggleTeam(teamId)} className="ml-1 hover:text-[#C8102E]" data-id="element-777">
                              ×
                            </button>
                          </span> : null;
                  })}
                    </div>
                  </div>}

                <div data-id="element-778">
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2" data-id="element-779">
                    Tell us a bit about yourself (Optional)
                  </label>
                  <textarea id="message" name="message" rows={4} value={formData.message} onChange={handleInputChange} placeholder="Any previous experience or specific areas you're passionate about?" className="w-full px-4 py-3 bg-[#F5F7FA] border border-[#E8ECF0] rounded-lg focus:outline-none focus:border-[#0A1F44] focus:ring-1 focus:ring-[#0A1F44] transition-colors resize-none" data-id="element-780" />
                </div>
              </div>

              <div className="flex flex-col items-center" data-id="element-781">
                <Button type="submit" size="lg" className="w-full md:w-auto" disabled={selectedTeams.length === 0} leftIcon={<Send className="w-4 h-4" data-id="element-783" />} data-id="element-782">
                  Submit Application
                </Button>
                {selectedTeams.length === 0 && <p className="text-sm text-gray-500 mt-2" data-id="element-784">
                    Please select at least one team above to continue.
                  </p>}
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Questions CTA */}
      <section className="py-16 bg-[#C8102E]" data-id="element-785">
        <div className="container-custom text-center" data-id="element-786">
          <h2 className="text-3xl font-serif font-bold text-white mb-4" data-id="element-787">
            Have Questions?
          </h2>
          <p className="text-white/90 mb-8 max-w-xl mx-auto" data-id="element-788">
            Not sure where you'd fit best? We'd love to help you discover your
            gifts and find the perfect place to serve.
          </p>
          <Link to="/contact" data-id="element-789">
            <Button variant="white" size="lg" className="text-[#C8102E]" rightIcon={<ArrowRight className="w-5 h-5" data-id="element-791" />} data-id="element-790">
              Contact Us
            </Button>
          </Link>
        </div>
      </section>
    </Layout>;
}